import React from 'react';
import { Settings, Shield, Eye, Database } from 'lucide-react';

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function properly.',
      examples: [
        'Session management and user authentication',
        'Security and fraud prevention',
        'Load balancing and website performance',
        'Basic website functionality and navigation'
      ],
      necessity: 'Required'
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website.',
      examples: [
        'Google Analytics for website traffic analysis',
        'User behavior tracking and heatmaps',
        'Page views and session duration',
        'Conversion tracking and goal completion'
      ],
      necessity: 'Optional'
    },
    {
      icon: <Settings className="h-8 w-8" />,
      title: 'Functional Cookies',
      description: 'These cookies enhance your experience by remembering your preferences.',
      examples: [
        'Language and region preferences',
        'Theme and display settings',
        'Form data and user inputs',
        'Personalized content and recommendations'
      ],
      necessity: 'Optional'
    },
    {
      icon: <Database className="h-8 w-8" />,
      title: 'Marketing Cookies',
      description: 'These cookies are used to deliver relevant advertisements and track campaigns.',
      examples: [
        'Social media integration and sharing',
        'Advertising campaign tracking',
        'Remarketing and targeted ads',
        'Email marketing and newsletter signups'
      ],
      necessity: 'Optional'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-purple-100 border border-white/30">
                🍪 Cookie Information
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              Cookie <span className="text-purple-300 bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-purple-100 leading-relaxed max-w-4xl mx-auto">
              Learn about how we use cookies to enhance your browsing experience and provide personalized services.
            </p>
            
            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Essential</div>
                <div className="text-purple-200 text-sm">Cookies</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Analytics</div>
                <div className="text-purple-200 text-sm">Tracking</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Your</div>
                <div className="text-purple-200 text-sm">Control</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Transparent</div>
                <div className="text-purple-200 text-sm">Usage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Cookies */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-purple-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              📅 Last Updated: December 2024
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              What Are <span className="text-purple-600 dark:text-purple-400">Cookies?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better browsing experience and understand how you use our site.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">How Cookies Work</h3>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li>• Cookies are created when you visit our website</li>
                    <li>• They store information about your preferences</li>
                    <li>• They help us remember your settings</li>
                    <li>• They improve website performance and security</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Cookie Benefits</h3>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li>• Personalized user experience</li>
                    <li>• Faster page loading times</li>
                    <li>• Improved website functionality</li>
                    <li>• Better security and fraud prevention</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Types */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-purple-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              🍪 Cookie Types
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Types of <span className="text-purple-600 dark:text-purple-400">Cookies We Use</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We use different types of cookies to provide you with the best possible experience on our website.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {cookieTypes.map((cookie, index) => (
              <div key={index} className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-purple-200 dark:hover:border-purple-600 hover:-translate-y-1 hover:scale-102 mx-4">
                {/* Header with Gradient */}
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 text-white relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors duration-300"></div>
                  
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mr-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                          {cookie.icon}
                        </div>
                        <h3 className="text-xl font-bold">{cookie.title}</h3>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        cookie.necessity === 'Required' 
                          ? 'bg-red-500/20 text-red-200' 
                          : 'bg-yellow-500/20 text-yellow-200'
                      }`}>
                        {cookie.necessity}
                      </span>
                    </div>
                    <p className="text-sm opacity-90 leading-relaxed">{cookie.description}</p>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-3 right-3 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-pulse">
                    🍪
                  </div>
                  <div className="absolute bottom-3 left-3 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    ⚙️
                  </div>
                </div>
                
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Examples:</h4>
                  <div className="space-y-2">
                    {cookie.examples.map((example, exampleIndex) => (
                      <div key={exampleIndex} className="flex items-start group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                        <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform duration-300"></div>
                        <span className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">{example}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cookie Management */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium mb-6">
              ⚙️ Cookie Control
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Manage Your <span className="text-blue-600 dark:text-blue-400">Cookie Preferences</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              You have control over which cookies you accept. Learn how to manage your cookie preferences.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Browser Settings</h3>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li>• Chrome: Settings → Privacy → Cookies</li>
                    <li>• Firefox: Options → Privacy → Cookies</li>
                    <li>• Safari: Preferences → Privacy</li>
                    <li>• Edge: Settings → Cookies and site permissions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Cookie Consent</h3>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li>• Accept all cookies for full functionality</li>
                    <li>• Reject optional cookies for privacy</li>
                    <li>• Customize preferences by category</li>
                    <li>• Change settings anytime</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-purple-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              📞 Cookie Questions
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Questions About <span className="text-purple-600 dark:text-purple-400">Cookies?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              If you have any questions about our cookie usage, please don't hesitate to contact us.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Privacy Team</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">For cookie and privacy inquiries:</p>
                  <a href="mailto:privacy@infurasoft.com" className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold">
                    privacy@infurasoft.com
                  </a>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Technical Support</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">For technical cookie issues:</p>
                  <a href="mailto:support@infurasoft.com" className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold">
                    support@infurasoft.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-purple-600 via-indigo-700 to-blue-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-purple-100 border border-white/30 mb-6">
              🍪 Your Cookie Control
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Transparent Cookie <span className="text-purple-300 bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent">Usage</span>
          </h2>
          <p className="text-xl lg:text-2xl text-purple-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            We believe in transparent cookie usage and giving you full control over your privacy preferences.
          </p>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Your</div>
              <div className="text-purple-200 text-sm">Control</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Transparent</div>
              <div className="text-purple-200 text-sm">Usage</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Privacy</div>
              <div className="text-purple-200 text-sm">First</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
